import React, { useState } from "react";
import axios from "axios";
import "../styles/Edit.css";
import backendUrl from "../BackendUrl";

const TrimSpecsManager = ({ trimId, trimSpecs, onUpdate }) => {
  const [isCanvasOpen, setCanvasOpen] = useState(false);
  const [selectedSpecIndex, setSelectedSpecIndex] = useState(null);
  const [specData, setSpecData] = useState({ spec: "", specDesc: [] });
  const [isNewSpec, setIsNewSpec] = useState(false);
  const adminToken = localStorage.getItem('adminToken');

  const openCanvas = () => {
    setCanvasOpen(true);
  };

  const closeCanvas = () => {
    setCanvasOpen(false);
    setSelectedSpecIndex(null);
    setSpecData({ spec: "", specDesc: [] });
    setIsNewSpec(false);
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (value === "new") {
      // Creating a new spec
      setIsNewSpec(true);
      setSelectedSpecIndex(null);
      setSpecData({ spec: "", specDesc: [] });
    } else {
      // Editing an existing spec
      setIsNewSpec(false);
      const index = parseInt(value, 10);
      setSelectedSpecIndex(index);
      const existingSpec = trimSpecs[index];
      // Ensure specDesc is an array:
      const specDescArray = Array.isArray(existingSpec.specDesc)
        ? existingSpec.specDesc
        : existingSpec.specDesc
        ? [existingSpec.specDesc]
        : [];
      setSpecData({ spec: existingSpec.spec || "", specDesc: specDescArray });
    }
  };

  const handleInputChange = (e, index = null) => {
    const { name, value } = e.target;
    if (name === "spec") {
      setSpecData((prev) => ({ ...prev, spec: value }));
    } else {
      const newSpecDesc = [...specData.specDesc];
      newSpecDesc[index] = value;
      setSpecData((prev) => ({ ...prev, specDesc: newSpecDesc }));
    }
  };

  const addDescriptionField = () => {
    setSpecData((prev) => ({ ...prev, specDesc: [...prev.specDesc, ""] }));
  };

  const removeDescriptionField = (index) => {
    const newSpecDesc = specData.specDesc.filter((_, i) => i !== index);
    setSpecData((prev) => ({ ...prev, specDesc: newSpecDesc }));
  };

  const handleSubmit = async () => {
    if (!specData.spec.trim()) {
      alert("Please enter a valid spec name.");
      return;
    }

    try {
      let response;
      let updatedSpecs;

      if (isNewSpec) {
        // Create a new spec object and post it
        const newSpec = { spec: specData.spec, specDesc: specData.specDesc };
        response = await axios.post(
          `${backendUrl}/api/vehicles/add-trim-spec/${trimId}`,
          { specs: [newSpec] }, {headers: {Authorization: `Bearer ${adminToken}`,},}
        );
        // API returns the updated trim with specs property
        updatedSpecs = response.data.trim.specs;
      } else {
        // Replace the existing spec in the entire specs array
        updatedSpecs = trimSpecs.map((s, index) =>
          index === selectedSpecIndex
            ? { spec: specData.spec, specDesc: specData.specDesc }
            : s
        );
        // API expects the complete specs array for update
        const specId = trimSpecs[selectedSpecIndex]._id;
        response = await axios.put(
          `${backendUrl}/api/vehicles/update-trim-spec/${trimId}/${specId}`,
          { specs: updatedSpecs }, {headers: {Authorization: `Bearer ${adminToken}`,},}
        );
        updatedSpecs = response.data.trim.specs;
      }

      onUpdate(updatedSpecs);
      closeCanvas();
      alert(isNewSpec ? "Spec added successfully!" : "Spec updated successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to save changes. Please try again.");
    }
  };

  // Handle deletion of a spec (including backend deletion)
  const handleSpecDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this spec?")) return;
    try {
      const specId = trimSpecs[selectedSpecIndex]._id;
      const response = await axios.delete(
        `${backendUrl}/api/vehicles/delete-trim-spec/${trimId}/${specId}`, {headers: {Authorization: `Bearer ${adminToken}`,},}
      );
      // Assume API returns the updated trim with specs property
      const updatedSpecs = response.data.trim.specs;
      onUpdate(updatedSpecs);
      closeCanvas();
      alert("Spec deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to delete spec. Please try again.");
    }
  };

  return (
    <div>
      <button type="button" onClick={openCanvas}>
        Edit Spec
      </button>

      {isCanvasOpen && (
        <div className="canvas-overlay">
          <div className="canvas">
            <h2>{isNewSpec ? "Add New Spec" : "Edit Spec"}</h2>

            {/* Dropdown for selecting a spec */}
            <label>
              Select Spec:
              <select onChange={handleSelectChange} value={selectedSpecIndex ?? ""}>
                <option value="">Select a spec...</option>
                {trimSpecs.map((spec, index) => (
                  <option key={index} value={index}>
                    {spec.spec || `Spec ${index + 1}`}
                  </option>
                ))}
                <option value="new">+ Add New Spec</option>
              </select>
            </label>

            {/* Show edit fields when a spec is selected */}
            {(isNewSpec || selectedSpecIndex !== null) && (
              <>
                <label>
                  Spec Name:
                  <input
                    type="text"
                    name="spec"
                    value={specData.spec}
                    onChange={handleInputChange}
                  />
                </label>

                <h3>Descriptions:</h3>
                {specData.specDesc.map((desc, index) => (
                  <div key={index} className="desc-input">
                    <input
                      type="text"
                      value={desc}
                      onChange={(e) => handleInputChange(e, index)}
                    />
                    <button type="button" onClick={() => removeDescriptionField(index)}>
                      Delete
                    </button>
                  </div>
                ))}

                <button type="button" onClick={addDescriptionField}>
                  + Add Description
                </button>

                <div className="canvas-buttons">
                  <button type="button" onClick={handleSubmit}>
                    {isNewSpec ? "Add Spec" : "Update Spec"}
                  </button>
                  {/* Render delete button only when editing an existing spec */}
                  {!isNewSpec && selectedSpecIndex !== null && (
                    <button type="button" onClick={handleSpecDelete}>
                      Delete Spec
                    </button>
                  )}
                  <button type="button" onClick={closeCanvas}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrimSpecsManager;